import React, { useCallback, useMemo, useState } from 'react';
import { Button, Result, message } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import DeliveryCompletedSummaryTable, { DeliveryCompletedSummaryRecord } from './components/DeliveryCompletedSummaryTable';
import DeliveryCompletedSummaryFilter, { DeliveryCompletedSummaryFilters } from './components/DeliveryCompletedSummaryFilter';
import DspDeliveryCompletedDetailDrawer from './components/DspDeliveryCompletedDetailDrawer';
import { isDspManager as isDspManagerRole } from '../../../../utils/auth';
import { useDspDeliveryCompletedSummaryByDriverGroupQuery } from './graphql/hooks/useDspDeliveryCompletedSummaryQuery';
import { useApolloClient, useMutation } from '@apollo/client';
import {
    GET_DSP_DELIVERY_COMPLETED_SUMMARY_BY_DRIVER_GROUP_PAGE_LIST,
    GetDspDeliveryCompletedSummaryByDriverGroupPageListResponse,
    DspDeliveryCompletedSummaryByDriverGroupType,
    DspDeliveryCompletedSummaryType,
} from './graphql/queries/getDspDeliveryCompletedSummaryList';
import {
    GENERATE_DSP_DELIVERY_COMPLETED_SUMMARY_TABLE_EXPORT,
    GenerateDspDeliveryCompletedSummaryTableExportResponse,
    GenerateDspDeliveryCompletedSummaryTableExportVariables,
} from './graphql/mutations/generateDspDeliveryCompletedSummaryTableExport';
import { dashboardTableExportCellString } from '../../../../utils/dashboardTableExportCellString';
import AsyncReportDownloadModal, { dashboardAsyncReportLabelI18nKeys, useAsyncReportExportCooldown } from '../../../../components/asyncReportDownload';
import {
    DSP_DELIVERY_COMPLETED_SUMMARY_FETCH_PAGE_SIZE,
    DSP_DELIVERY_COMPLETED_SUMMARY_EXPORT_MAX_ROWS,
    DSP_DELIVERY_COMPLETED_SUMMARY_EXPORT_FETCH_THROTTLE_MS,
    DSP_DELIVERY_COMPLETED_SUMMARY_EXPORT_MAX_FETCH_PAGES,
    mapDspSummaryRowToCells,
    sleepMs,
    yieldToMain,
} from './utils/dspDeliveryCompletedSummaryExport';

/**
 * delivery-completed-summary 页面
 */
const DeliveryCompletedSummaryPage: React.FC = () => {
    const { t } = useTranslation();
    const apolloClient = useApolloClient();
    const { exportLoading, runExportWithCooldown, getExportButtonLabel } = useAsyncReportExportCooldown();
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');
    const [generateDashboardTableExport] = useMutation<
        GenerateDspDeliveryCompletedSummaryTableExportResponse,
        GenerateDspDeliveryCompletedSummaryTableExportVariables
    >(GENERATE_DSP_DELIVERY_COMPLETED_SUMMARY_TABLE_EXPORT);
    const isDspOnlyVisible = useMemo(() => isDspManagerRole(), []);
    
    // 初始化筛选条件
    const [filters, setFilters] = useState<DeliveryCompletedSummaryFilters>(() => ({
        dateRange: [dayjs().subtract(7, 'day'), dayjs()]
    }));

    const {
        records,
        loading,
        pagination,
        handleFilter
    } = useDspDeliveryCompletedSummaryByDriverGroupQuery();

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<DeliveryCompletedSummaryRecord | null>(null);

    // 转换数据格式
    const tableData: DeliveryCompletedSummaryRecord[] = useMemo(() => {
        return records.map(record => ({
            deliveredDate: record.deliveredDate,
            hubId: record.hubId ?? 0,
            hubName: record.hubName || '',
            warehouseId: record.warehouseId ?? 0,
            warehouseName: record.warehouseName || '',
            areaId: record.areaId ?? 0,
            areaName: record.areaName || '',
            dspId: record.dspId ?? 0,
            dspName: record.dspName || '',
            dspFullName: record.dspFullName || '',
            dspContact: record.dspContact || '',
            driverId: record.driverId,
            driverName: record.driverName || '',
            driverPhone: record.driverPhone || '',
            deliveredCount: record.deliveredCount ?? 0,
            standardRateDeliveredCount: record.standardRateDeliveredCount ?? 0,
            discountRateDeliveredCount: record.discountRateDeliveredCount ?? 0
        }));
    }, [records]);

    // 处理筛选变化
    const handleFiltersChange = (newFilters: Partial<DeliveryCompletedSummaryFilters>) => {
        const updatedFilters = { ...filters, ...newFilters };
        setFilters(updatedFilters);
        handleFilter(updatedFilters, 1, pagination.size);
    };

    // 处理分页变化
    const handlePageChange = (newPage: number, newPageSize?: number) => {
        handleFilter(filters, newPage, newPageSize || pagination.size);
    };

    const handleExportSummary = useCallback(async () => {
        if (!filters.dateRange) {
            return;
        }
        if (exportLoading) {
            message.warning(t('wms.delivery.deliveryCompletedSummary.exportGenerating'));
            return;
        }

        await runExportWithCooldown(async () => {
            try {
                const dateRange = filters.dateRange;
                if (!dateRange) {
                    return;
                }
                const [start, end] = dateRange;
                const deliveredDateStart = start.format('YYYY-MM-DD');
                const deliveredDateEnd = end.format('YYYY-MM-DD');

                // 与列表查询同一 filter，按「司机组」分页拉全量
                const allGroups: DspDeliveryCompletedSummaryByDriverGroupType[] = [];

                let page = 1;
                while (true) {
                    const resp = await apolloClient.query<GetDspDeliveryCompletedSummaryByDriverGroupPageListResponse>({
                        query: GET_DSP_DELIVERY_COMPLETED_SUMMARY_BY_DRIVER_GROUP_PAGE_LIST,
                        variables: {
                            pageRequest: {
                                filter: {
                                    deliveredDateStart,
                                    deliveredDateEnd,
                                },
                                pageRequest: {
                                    page,
                                    size: DSP_DELIVERY_COMPLETED_SUMMARY_FETCH_PAGE_SIZE,
                                },
                            },
                        },
                        fetchPolicy: 'network-only',
                    });

                    const pageResponse = resp.data?.getDspDeliveryCompletedSummaryByDriverGroupPageList?.pageResponse;
                    if (!pageResponse) break;
                    const content = pageResponse.content ?? [];
                    if (content.length === 0) break;
                    allGroups.push(...content);

                    const totalItems = allGroups.reduce((acc, g) => acc + (g.items?.length ?? 0), 0);
                    if (totalItems > DSP_DELIVERY_COMPLETED_SUMMARY_EXPORT_MAX_ROWS) {
                        message.warning(
                            t('wms.delivery.deliveryCompletedSummary.exportMaxRowsExceeded', {
                                max: DSP_DELIVERY_COMPLETED_SUMMARY_EXPORT_MAX_ROWS,
                            })
                        );
                        return;
                    }
                    if (content.length < DSP_DELIVERY_COMPLETED_SUMMARY_FETCH_PAGE_SIZE) break;
                    if (pageResponse.hasNext === false) break;
                    const totalPages = pageResponse.totalPages;
                    if (typeof totalPages === 'number' && totalPages > 0 && page >= totalPages) break;
                    if (page >= DSP_DELIVERY_COMPLETED_SUMMARY_EXPORT_MAX_FETCH_PAGES) break;
                    page += 1;
                    await sleepMs(DSP_DELIVERY_COMPLETED_SUMMARY_EXPORT_FETCH_THROTTLE_MS);
                }

                await yieldToMain();

                allGroups.sort((a, b) => {
                    const diff = Number(b.totalDeliveredCount ?? 0) - Number(a.totalDeliveredCount ?? 0);
                    if (diff !== 0) return diff;
                    return Number(a.driverId ?? 0) - Number(b.driverId ?? 0);
                });

                const flatItems: DspDeliveryCompletedSummaryType[] = [];
                for (const g of allGroups) {
                    const items = [...(g.items || [])];
                    items.sort((a, b) => {
                        const dd = String(b.deliveredDate ?? '').localeCompare(String(a.deliveredDate ?? ''));
                        if (dd !== 0) return dd;
                        const dc = Number(b.deliveredCount ?? 0) - Number(a.deliveredCount ?? 0);
                        if (dc !== 0) return dc;
                        const ar = Number(a.areaId ?? 0) - Number(b.areaId ?? 0);
                        if (ar !== 0) return ar;
                        return Number(a.hubId ?? 0) - Number(b.hubId ?? 0);
                    });
                    flatItems.push(...items);
                    await yieldToMain();
                }

                await yieldToMain();

                if (flatItems.length === 0) {
                    message.warning(t('wms.delivery.deliveryCompletedSummary.exportNoData'));
                    return;
                }

                const p = 'wms.delivery.deliveryCompletedSummary.exportHeaders';
                const mainColumnHeaders: string[] = [
                    t(`${p}.deliveredDate`),
                    t(`${p}.dspName`),
                    t(`${p}.dspContact`),
                    t(`${p}.dspFullName`),
                    t(`${p}.driverId`),
                    t(`${p}.driverName`),
                    t(`${p}.driverPhone`),
                    t(`${p}.hubName`),
                    t(`${p}.warehouseName`),
                    t(`${p}.routeName`),
                    t(`${p}.deliveredQuantity`),
                    t(`${p}.standardRateDeliveredQuantity`),
                    t(`${p}.discountRateDeliveredQuantity`),
                ];
                const mainRows = flatItems.map((r) =>
                    mapDspSummaryRowToCells(r).map((cell) => dashboardTableExportCellString(cell))
                );
                const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const auditSummary = `dspDeliveryCompletedSummary ${deliveredDateStart}..${deliveredDateEnd}`;
                const response = await generateDashboardTableExport({
                    variables: {
                        input: {
                            fileNamePrefix: 'DSP_Delivery_Completed_Summary',
                            timezone: userTimezone,
                            snapshotSheetName: 'Sheet1',
                            mainSheetName: 'Sheet1',
                            snapshotRows: null,
                            mainColumnHeaders,
                            mainRows,
                            auditSummary,
                        },
                    },
                });
                const fileName = response.data?.generateDspDeliveryCompletedSummaryTableExport;
                if (fileName) {
                    setGeneratedFileName(fileName);
                    setDownloadCompleteModalVisible(true);
                } else {
                    message.error(t('wms.delivery.deliveryCompletedSummary.exportFailed'));
                }
            } catch (e: unknown) {
                const err = e as { message?: string; graphQLErrors?: Array<{ message?: string; extensions?: { message?: string } }> };
                const msg = String(err?.message || '');
                if (msg.includes('请求超限')) {
                    message.error(t('common.messages.rateLimited'));
                } else if (err?.graphQLErrors?.length) {
                    message.error(err.graphQLErrors[0].extensions?.message ?? err.graphQLErrors[0].message ?? t('wms.delivery.deliveryCompletedSummary.exportFailed'));
                } else {
                    message.error(err?.message || t('wms.delivery.deliveryCompletedSummary.exportFailed'));
                }
            }
        });
    }, [apolloClient, exportLoading, filters, generateDashboardTableExport, runExportWithCooldown, t]);

    if (!isDspOnlyVisible) {
        return (
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-3">
                <Result status="403" title="403" subTitle="Forbidden" />
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            {/* 筛选 + 操作区（与通用页面一致：左筛选，右按钮） */}
            <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                    <DeliveryCompletedSummaryFilter
                        filters={filters}
                        onFiltersChange={handleFiltersChange}
                        showActions={false}
                    />
                </div>

                <div className="flex justify-end">
                    <Button
                        type="primary"
                        onClick={() => void handleExportSummary()}
                        loading={exportLoading}
                        disabled={loading || exportLoading || !filters.dateRange}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{getExportButtonLabel(t('components.button.download'))}</span>
                    </Button>
                </div>
            </div>

            {/* 口径说明（样式参照 parcel-sla-monitoring） */}
            <div className="mb-4">
                <div
                    className="p-3 rounded"
                    style={{
                        backgroundColor: '#fff2f0',
                        border: '1px solid #ffccc7',
                        color: '#ff4d4f'
                    }}
                >
                    <div className="text-sm font-bold">
                        <strong>
                            {t('wms.delivery.deliveryCompletedSummary.definitionTitle')}:
                            {' '}
                            {t('wms.delivery.deliveryCompletedSummary.definition')}
                        </strong>
                    </div>
                </div>
            </div>

            {/* 表格区域 */}
            <DeliveryCompletedSummaryTable
                data={tableData}
                loading={loading}
                page={pagination.page}
                pageSize={pagination.size}
                total={pagination.totalElements}
                onPageChange={handlePageChange}
                onDeliveredCountClick={(record) => {
                    setSelectedRecord(record);
                    setDrawerOpen(true);
                }}
            />

            <DspDeliveryCompletedDetailDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                record={selectedRecord}
                dateRange={
                    filters.dateRange
                        ? [filters.dateRange[0].format('YYYY-MM-DD'), filters.dateRange[1].format('YYYY-MM-DD')]
                        : null
                }
            />
            <AsyncReportDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
                reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.dspDeliveryCompletedSummary}
            />
        </div>
    );
};

export default DeliveryCompletedSummaryPage;

